import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MapPin, Search, Sparkles, Map as MapIcon, ArrowUpRight } from "lucide-react";
import CategoryIcon from "../components/CategoryIcon";
import { CATEGORIES, CATEGORY_ORDER } from "../lib/categories";
import { CHAD_REGIONS, MAJOR_CITIES } from "../lib/chad";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/wingman/7e1b41d7-cb47-4627-a363-d0e2bc80c154/attachments/543013c3b3be461597c3590446b98b96_image.png";

const DISCOVER_IMAGES = {
  guelta:   "https://customer-assets.emergentagent.com/wingman/7e1b41d7-cb47-4627-a363-d0e2bc80c154/attachments/b2ec5df0cb6440d5af72da485dfd35f8_image.png",
  wildlife: "https://customer-assets.emergentagent.com/wingman/7e1b41d7-cb47-4627-a363-d0e2bc80c154/attachments/4efaae2898c64f4da374f420b642ea87_image.png",
  camels:   "https://customer-assets.emergentagent.com/wingman/7e1b41d7-cb47-4627-a363-d0e2bc80c154/attachments/265f59e028914dc49af8dac3af96a1a8_image.png",
  ounianga: "https://customer-assets.emergentagent.com/wingman/7e1b41d7-cb47-4627-a363-d0e2bc80c154/attachments/9d40791ea6bd462d9fe2150895a9d6f7_image.png",
  ennedi:   "https://customer-assets.emergentagent.com/wingman/7e1b41d7-cb47-4627-a363-d0e2bc80c154/attachments/c02a776eabd340d39f3d2aa8e00fdc96_image.png",
};

export default function HomePage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [city, setCity] = useState("all");
  const [stats, setStats] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/places/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (city && city !== "all") params.set("city", city);
    navigate(`/carte?${params.toString()}`);
  };

  const totalCount = Object.values(stats).reduce((a, b) => a + (b || 0), 0);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Tchad" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-28 sm:pt-28 sm:pb-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-gold/95 backdrop-blur-md text-brand-black text-xs uppercase tracking-[0.2em] font-bold mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              {t("brand.tagline")}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-5">
              {t("home.hero.title.line1")}{" "}
              <span className="text-brand-gold">{t("home.hero.title.line2")}</span>
            </h1>
            <p className="text-base sm:text-lg text-white/90 max-w-xl mb-8 leading-relaxed">
              {t("home.hero.subtitle")}
            </p>

            <form
              onSubmit={handleSearch}
              className="bg-white rounded-2xl p-2 sm:p-3 shadow-2xl flex flex-col sm:flex-row gap-2 sm:gap-1.5"
              data-testid="hero-search-form"
            >
              <div className="flex-1 flex items-center gap-2 px-3">
                <Search className="w-4 h-4 text-brand-inkMuted shrink-0" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("home.hero.search.placeholder")}
                  className="border-0 shadow-none focus-visible:ring-0 px-0 h-10 text-brand-ink"
                  data-testid="hero-search-input"
                />
              </div>
              <div className="sm:w-52">
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger
                    className="border-0 shadow-none focus:ring-0 h-12 bg-brand-sandlight rounded-xl"
                    data-testid="hero-city-select"
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-brand-inkMuted" />
                      <SelectValue placeholder={t("common.allCities")} />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.allCities")}</SelectItem>
                    {MAJOR_CITIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                className="h-12 px-7 rounded-xl bg-brand-terracotta hover:bg-brand-terracottaHover text-white font-semibold"
                data-testid="hero-search-submit"
              >
                {t("home.hero.search.cta")}
              </Button>
            </form>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-white/85 text-sm">
              <Link to="/carte" className="inline-flex items-center gap-2 hover:text-white underline-offset-4 hover:underline">
                <MapIcon className="w-4 h-4" />
                {t("home.hero.viewMap")}
              </Link>
              <span className="opacity-60">·</span>
              <span>{t("home.hero.listingsCount", { count: totalCount })}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Categories grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-terracotta font-semibold mb-2">
              {t("home.categories.label")}
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-ink tracking-tight">
              {t("home.categories.title")}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {CATEGORY_ORDER.map((key, i) => {
            const cat = CATEGORIES[key];
            const count = stats[key] || 0;
            return (
              <Link
                key={key}
                to={`/${cat.slug}`}
                className="group relative overflow-hidden rounded-2xl border border-brand-line bg-white p-5 sm:p-6 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(45,42,38,0.08)] transition-all duration-300 fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`category-tile-${key}`}
              >
                <div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center mb-4 text-white shadow-sm transition-transform group-hover:scale-110"
                  style={{ backgroundColor: cat.color }}
                >
                  <CategoryIcon name={cat.icon} className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <div className="font-heading font-semibold text-base sm:text-lg text-brand-ink mb-1 leading-tight">
                  {t(`cat.${key}`)}
                </div>
                <p className="text-xs sm:text-sm text-brand-inkSoft leading-relaxed line-clamp-2 mb-3">
                  {cat.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-brand-inkMuted">
                    {t("home.tile.count", { count })}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-brand-inkMuted group-hover:text-brand-terracotta group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
              </Link>
            );
          })}

          {/* Map tile */}
          <Link
            to="/carte"
            className="group relative overflow-hidden rounded-2xl p-5 sm:p-6 hover:-translate-y-1 transition-all duration-300 fade-up text-white"
            style={{
              animationDelay: `${CATEGORY_ORDER.length * 60}ms`,
              backgroundImage:
                "linear-gradient(135deg, #2D2A26 0%, #5C5750 100%)",
            }}
            data-testid="category-tile-map"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
              <MapIcon className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={1.8} />
            </div>
            <div className="font-heading font-semibold text-base sm:text-lg mb-1 leading-tight">
              {t("home.tile.map.title")}
            </div>
            <p className="text-xs sm:text-sm text-white/80 leading-relaxed mb-3">
              {t("home.tile.map.subtitle")}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-white/70">{t("home.tile.map.cta")}</span>
              <ArrowUpRight className="w-4 h-4 text-white/70 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
            </div>
          </Link>
        </div>
      </section>

      {/* Discover Chad — landscapes & wildlife gallery */}
      <section className="bg-brand-cream border-y border-brand-line py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-2">
                {t("home.discover.label")}
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-ink tracking-tight mb-3">
                {t("home.discover.title")}
              </h2>
              <p className="text-sm sm:text-base text-brand-inkSoft leading-relaxed">
                {t("home.discover.subtitle")}
              </p>
            </div>
            <Link to="/agences-de-voyages" className="shrink-0">
              <Button variant="outline" className="rounded-full border-brand-line text-brand-ink hover:bg-brand-sandlight">
                {t("home.discover.cta")} <ArrowUpRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>

          {/* Magazine-style grid: 1 large + 4 small */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:grid-rows-2 lg:[grid-template-rows:repeat(2,260px)]">
            {/* Large feature */}
            <Link
              to="/agences-de-voyages"
              className="group relative overflow-hidden rounded-2xl sm:col-span-2 lg:row-span-2 lg:col-span-2 fade-up"
              data-testid="discover-tile-ennedi"
            >
              <img
                src={DISCOVER_IMAGES.ennedi}
                alt={t("home.discover.ennedi")}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              <div className="relative h-full min-h-[280px] flex flex-col justify-end p-5 sm:p-7 text-white">
                <div className="text-[10px] uppercase tracking-[0.2em] text-brand-gold font-bold mb-1.5">
                  {t("home.discover.ennedi.region")}
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-semibold leading-tight">
                  {t("home.discover.ennedi")}
                </h3>
              </div>
            </Link>

            {/* Small tiles */}
            {[
              { key: "guelta", img: DISCOVER_IMAGES.guelta, testId: "discover-tile-guelta" },
              { key: "ounianga", img: DISCOVER_IMAGES.ounianga, testId: "discover-tile-ounianga" },
              { key: "camels", img: DISCOVER_IMAGES.camels, testId: "discover-tile-camels" },
              { key: "wildlife", img: DISCOVER_IMAGES.wildlife, testId: "discover-tile-wildlife" },
            ].map((tile, i) => (
              <Link
                key={tile.key}
                to="/agences-de-voyages"
                className="group relative overflow-hidden rounded-2xl fade-up"
                style={{ animationDelay: `${(i + 1) * 80}ms` }}
                data-testid={tile.testId}
              >
                <img
                  src={tile.img}
                  alt={t(`home.discover.${tile.key}`)}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
                <div className="relative h-full min-h-[220px] flex flex-col justify-end p-4 text-white">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-brand-gold font-bold mb-1">
                    {t(`home.discover.${tile.key}.region`)}
                  </div>
                  <h3 className="font-heading text-base sm:text-lg font-semibold leading-tight">
                    {t(`home.discover.${tile.key}`)}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cities band */}
      <section className="relative bg-brand-black text-white py-14 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={DISCOVER_IMAGES.guelta}
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-brand-black/85 to-brand-black/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-3">
            {t("home.cities.label")}
          </div>
          <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-6 tracking-tight">
            {t("home.cities.title")}
          </h3>
          <div className="flex flex-wrap gap-2" data-testid="home-cities-band">
            {MAJOR_CITIES.map((c) => (
              <Link
                key={c}
                to={`/carte?city=${encodeURIComponent(c)}`}
                className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-medium text-white hover:border-brand-gold hover:text-brand-gold hover:bg-brand-gold/10 transition-colors backdrop-blur-sm"
                data-testid={`city-chip-${c}`}
              >
                <MapPin className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                {c}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer — Comprehensive Regions & Cities */}
      <footer className="border-t border-brand-line bg-brand-ink text-white" data-testid="footer-regions-cities">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-px flex-1 bg-white/10" />
            <div className="text-[11px] uppercase tracking-[0.28em] text-brand-gold font-bold">
              {t("footer.regionsCities.label")}
            </div>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-center tracking-tight mb-2">
            {t("footer.regionsCities.title")}
          </h3>
          <p className="text-center text-sm text-white/60 max-w-2xl mx-auto mb-10">
            {t("footer.regionsCities.subtitle")}
          </p>

          {/* 23 Regions grid */}
          <div className="mb-12">
            <h4 className="text-xs uppercase tracking-[0.2em] text-brand-gold font-bold mb-4" data-testid="footer-regions-title">
              {t("footer.regions.title", { count: CHAD_REGIONS.length })}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2.5">
              {CHAD_REGIONS.map((r) => (
                <Link
                  key={r.region}
                  to={`/carte?city=${encodeURIComponent(r.capital)}`}
                  className="group flex items-start gap-2 py-1 text-sm hover:text-brand-gold transition-colors"
                  data-testid={`footer-region-${r.region.replace(/[^a-zA-Z]/g, "-")}`}
                >
                  <MapPin className="w-3.5 h-3.5 text-brand-gold/60 group-hover:text-brand-gold mt-0.5 shrink-0" />
                  <div className="flex flex-col leading-tight">
                    <span className="font-medium text-white group-hover:text-brand-gold">{r.region}</span>
                    <span className="text-[10px] uppercase tracking-wider text-white/40">
                      {t("footer.regions.capital")} · {r.capital}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Major cities chips */}
          <div className="mb-12">
            <h4 className="text-xs uppercase tracking-[0.2em] text-brand-gold font-bold mb-4" data-testid="footer-cities-title">
              {t("footer.cities.title", { count: MAJOR_CITIES.length })}
            </h4>
            <div className="flex flex-wrap gap-2">
              {MAJOR_CITIES.map((c) => (
                <Link
                  key={c}
                  to={`/carte?city=${encodeURIComponent(c)}`}
                  className="px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-white hover:border-brand-gold hover:text-brand-gold hover:bg-brand-gold/10 transition-colors"
                  data-testid={`footer-city-${c.replace(/[^a-zA-Z]/g, "-")}`}
                >
                  <MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />
                  {c}
                </Link>
              ))}
            </div>
          </div>

          {/* Bottom row: brand + copyright */}
          <div className="pt-8 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2.5">
              <img
                src="https://customer-assets.emergentagent.com/wingman/7e1b41d7-cb47-4627-a363-d0e2bc80c154/attachments/355f24cbb44947998348bd262690697f_image.png"
                alt="Kanama"
                className="h-7 w-7 object-contain"
              />
              <div>
                <span className="font-heading font-bold text-white">{t("brand.name")}</span>
                <p className="text-[10px] uppercase tracking-[0.18em] text-brand-gold font-semibold">
                  {t("brand.tagline.short")}
                </p>
              </div>
            </div>
            <p className="text-xs text-white/50">
              {t("footer.copyright", { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
